// @file Shared warm/capture helpers for the main Padlet PostHog project — the client in
// vuecomposables/posthog (the one the NPS survey and session recording use), not the arcade
// instance in bits/posthog.ts.
//
// Any surface may capture into that project through here, so the transport behavior (beacon,
// error reporting) can never drift between callers. Both helpers take a caller-supplied
// `source` so a failure is attributed to the calling surface, not to this module.
import { captureException } from '@@/bits/error_tracker'
import type { PostHog } from 'posthog-js'

// Imported at call time rather than at module scope. Callers of this module sit in boot-path chunks
// (e.g. the landside navigation store), and a static edge into the composable puts the whole PostHog
// SDK on those pages' boot path.
async function loadPostHog(): Promise<{ posthog: PostHog }> {
  const { usePostHog } = await import('@@/vuecomposables/posthog')
  return await usePostHog()
}

/**
 * Kick off the lazy PostHog client load ahead of the first capture. The client lives in a
 * dynamically imported chunk; if that import only starts inside a click/submit handler, the
 * page can unload before the chunk arrives and the capture is silently lost. Instrumented
 * surfaces call this on mount so the client is ready by the time an event can fire. Safe to
 * call repeatedly — usePostHog caches one init per page load and retries after a failed one.
 */
function warmPostHog(source: string): void {
  loadPostHog().catch((error) => {
    captureException(error, { context: { source, event: 'warm_posthog' } })
  })
}

/**
 * Capture an event into the main Padlet PostHog project with the beacon transport and
 * skip-the-batch: most conversion events race a navigation, and a queued XHR/fetch dies with
 * the document while a beacon survives the unload.
 *
 * Fire-and-forget: the client inits lazily on first capture, so surfaces whose events race
 * a navigation must warm it up front (see warmPostHog). A failure must never break the
 * calling UI — but it is reported (under the caller's `source`), not swallowed. The returned
 * promise settles once the capture has been handed to the beacon transport and never rejects,
 * so a caller that is about to tear the document down itself (e.g. a programmatic reload) can
 * wait for it; everyone else ignores it.
 */
function capturePostHogEvent(event: string, properties: Record<string, unknown>, source: string): Promise<void> {
  return loadPostHog()
    .then(({ posthog }) => {
      posthog.capture(event, properties, { transport: 'sendBeacon', send_instantly: true })
    })
    .catch((error) => {
      captureException(error, { context: { source, event } })
    })
}

export { capturePostHogEvent, warmPostHog }
